import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { CheckPaymentDetails } from '../payment-method-selection-dialog';

type CheckDraft = {
  referenceId: string;
  amount: number | null;
  bankNumber: string;
  branchNumber: string;
  accountNumber: string;
  dueDate?: Date;
};

interface AddCheckDialogProps {
  visible: boolean;
  onHide: () => void;
  initialDraft?: Partial<CheckDraft>;
  headerText?: string;
  submitLabel?: string;
  submitIcon?: string;
  onSubmit: (check: CheckPaymentDetails) => void;
}

export const AddCheckDialog: React.FC<AddCheckDialogProps> = ({
  visible,
  onHide,
  initialDraft,
  headerText = 'הוספת המחאה',
  submitLabel = 'הוסף',
  submitIcon = 'pi pi-plus',
  onSubmit,
}) => {
  const isEditMode = submitLabel !== 'הוסף' || headerText !== 'הוספת המחאה';
  const [draft, setDraft] = useState<CheckDraft>({
    referenceId: '',
    amount: null,
    bankNumber: '',
    branchNumber: '',
    accountNumber: '',
    dueDate: undefined,
  });

  useEffect(() => {
    if (!visible) return;
    setDraft({
      referenceId: initialDraft?.referenceId ?? '',
      amount: initialDraft?.amount ?? null,
      bankNumber: initialDraft?.bankNumber ?? '',
      branchNumber: initialDraft?.branchNumber ?? '',
      accountNumber: initialDraft?.accountNumber ?? '',
      dueDate: initialDraft?.dueDate,
    });
  }, [
    visible,
    initialDraft?.referenceId,
    initialDraft?.amount,
    initialDraft?.bankNumber,
    initialDraft?.branchNumber,
    initialDraft?.accountNumber,
    initialDraft?.dueDate,
  ]);

  const validation = useMemo(() => {
    const referenceId = (draft.referenceId ?? '').trim();
    const amount = typeof draft.amount === 'number' ? draft.amount : null;
    const bankNumber = (draft.bankNumber ?? '').trim();
    const branchNumber = (draft.branchNumber ?? '').trim();
    const accountNumber = (draft.accountNumber ?? '').trim();
    const dueDate = draft.dueDate;

    return {
      referenceId,
      amount,
      bankNumber,
      branchNumber,
      accountNumber,
      dueDate,
      isValid: Boolean(
        referenceId &&
          amount &&
          amount > 0 &&
          bankNumber &&
          branchNumber &&
          accountNumber &&
          dueDate,
      ),
    };
  }, [draft]);

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: isEditMode
                ? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)'
                : 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
              color: isEditMode ? '#0369a1' : '#6d28d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            }}
            aria-hidden
          >
            <i className={isEditMode ? 'pi pi-pencil' : 'pi pi-plus'} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: '16px',
                color: '#0f172a',
                letterSpacing: '-0.015em',
                lineHeight: 1.25,
              }}
            >
              {headerText}
            </div>
            <div
              style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}
            >
              נא להזין פרטי המחאה כפי שמופיעים על גבי ההמחאה / פרטי החשבון
            </div>
          </div>
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '760px', maxWidth: '96vw' }}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
        >
          <Button
            type="button"
            label="ביטול"
            className="p-button-text"
            onClick={onHide}
          />
          <Button
            type="button"
            label={submitLabel}
            icon={submitIcon}
            className="p-button-raised"
            disabled={!validation.isValid}
            onClick={() => {
              if (!validation.isValid) return;
              onSubmit({
                referenceId: validation.referenceId,
                amount: validation.amount!,
                bankNumber: validation.bankNumber,
                branchNumber: validation.branchNumber,
                accountNumber: validation.accountNumber,
                dueDate: draft.dueDate,
                allocations: [],
              });
              onHide();
            }}
          />
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '12px 12px',
          borderRadius: '10px',
          border: '1px solid #bae6fd',
          background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%)',
          color: '#075985',
          marginBottom: '14px',
        }}
      >
        <i
          className="pi pi-info-circle"
          style={{ marginTop: '2px' }}
          aria-hidden
        />
        <div style={{ fontSize: '12px', lineHeight: 1.55 }}>
          שדות חובה: <strong>מספר המחאה</strong>, <strong>בנק</strong>,{' '}
          <strong>סכום</strong>, <strong>סניף</strong>, <strong>חשבון</strong>,{' '}
          <strong>תאריך פירעון</strong>.
        </div>
      </div>
      <div
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '14px',
          padding: '16px 16px',
          boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Row 1: מספר המחאה + סכום + בנק */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                מספר המחאה <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.referenceId}
                onChange={(e) =>
                  setDraft({ ...draft, referenceId: e.target.value })
                }
                placeholder="הזן מספר המחאה"
                style={{ borderRadius: '10px', width: '100%' }}
                className={classNames({
                  'p-invalid': visible && !validation.referenceId,
                })}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                סכום <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputNumber
                value={draft.amount}
                onChange={(e) =>
                  setDraft({ ...draft, amount: e.value ?? null })
                }
                prefix="₪"
                inputStyle={{ width: '100%', direction: 'rtl' }}
                style={{ width: '100%' }}
                min={0}
                maxFractionDigits={2}
                locale="he-IL"
                placeholder="הזן סכום"
                className={classNames({
                  'p-invalid': visible && !validation.amount,
                })}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                בנק <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.bankNumber}
                onChange={(e) =>
                  setDraft({ ...draft, bankNumber: e.target.value })
                }
                placeholder="הזן מספר בנק"
                style={{ borderRadius: '10px', width: '100%' }}
                className={classNames({
                  'p-invalid': visible && !validation.bankNumber,
                })}
              />
            </div>
          </div>

          {/* Row 2: סניף + חשבון + תאריך פירעון */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                סניף <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.branchNumber}
                onChange={(e) =>
                  setDraft({ ...draft, branchNumber: e.target.value })
                }
                placeholder="הזן מספר סניף"
                style={{ borderRadius: '10px', width: '100%' }}
                className={classNames({
                  'p-invalid': visible && !validation.branchNumber,
                })}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                חשבון <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <InputText
                value={draft.accountNumber}
                onChange={(e) =>
                  setDraft({ ...draft, accountNumber: e.target.value })
                }
                placeholder="הזן מספר חשבון"
                style={{ borderRadius: '10px', width: '100%' }}
                className={classNames({
                  'p-invalid': visible && !validation.accountNumber,
                })}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <label className="mb-2 font-medium">
                תאריך פירעון <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <Calendar
                style={{ direction: 'ltr', width: '100%' }}
                value={draft.dueDate ?? null}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    dueDate: (e.value as Date) || undefined,
                  })
                }
                inputStyle={{ direction: 'rtl', width: '100%' }}
                showIcon
                iconPos="right"
                dateFormat="dd/mm/yy"
                className={classNames({
                  'p-invalid': visible && !validation.dueDate,
                })}
              />
              <div
                style={{
                  fontSize: '11px',
                  color: '#94a3b8',
                  marginTop: '6px',
                }}
              >
                חובה לבחור תאריך פירעון.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
